import React from 'react';

/**
 * Time Window Selector Component
 * 
 * Provides 12M / 24M / 36M / All filter buttons for chart views.
 * This is a PRESENTATION-LAYER filter only - does NOT affect domain logic.
 */

const TIME_WINDOWS = [
  { key: '12m', label: '12M', months: 12 },
  { key: '24m', label: '24M', months: 24 },
  { key: '36m', label: '36M', months: 36 },
  { key: 'all', label: 'All', months: null }
];

const TimeWindowSelector = ({ selected = 'all', onChange }) => {
  return (
    <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
      {TIME_WINDOWS.map(window => (
        <button
          key={window.key}
          onClick={() => onChange(window.key)}
          className={`
            px-3 py-1.5 text-xs font-medium rounded-md transition-all
            ${selected === window.key
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }
          `}
        >
          {window.label}
        </button>
      ))}
    </div>
  );
};

/**
 * Filter results by time window
 * 
 * @param {Array} results - Array of result records with date field
 * @param {string} windowKey - '12m', '24m', '36m', or 'all'
 * @returns {Array} Filtered results within the time window
 */
export const filterByTimeWindow = (results, windowKey) => {
  if (!results || results.length === 0 || windowKey === 'all') {
    return results;
  }
  
  const window = TIME_WINDOWS.find(w => w.key === windowKey);
  if (!window || !window.months) {
    return results;
  }
  
  // Find the most recent result date (windowEnd)
  const sortedDates = results
    .map(r => new Date(r.date).getTime())
    .filter(ts => !isNaN(ts))
    .sort((a, b) => b - a);
  
  if (sortedDates.length === 0) {
    return results;
  }
  
  const windowEnd = sortedDates[0];
  const windowStart = new Date(windowEnd);
  windowStart.setMonth(windowStart.getMonth() - window.months);
  const windowStartTs = windowStart.getTime();
  
  // Filter results within the window
  return results.filter(r => {
    const ts = new Date(r.date).getTime();
    return !isNaN(ts) && ts >= windowStartTs && ts <= windowEnd;
  });
};

/**
 * Filter treatments by time window
 * 
 * @param {Array} treatments - Array of treatment records with start_date and end_date
 * @param {Array} results - Array of result records (to determine windowEnd)
 * @param {string} windowKey - '12m', '24m', '36m', or 'all'
 * @returns {Array} Treatments that overlap with the time window
 */
export const filterTreatmentsByTimeWindow = (treatments, results, windowKey) => {
  if (!treatments || treatments.length === 0 || windowKey === 'all') {
    return treatments;
  }
  
  if (!results || results.length === 0) {
    return treatments;
  }
  
  const window = TIME_WINDOWS.find(w => w.key === windowKey);
  if (!window || !window.months) {
    return treatments;
  }
  
  // Window ends at TODAY (not most recent result) - matches chart domain
  const windowEnd = Date.now();
  const windowStart = new Date(windowEnd);
  windowStart.setMonth(windowStart.getMonth() - window.months);
  const windowStartTs = windowStart.getTime();
  
  // Filter treatments that overlap with the window
  return treatments.filter(t => {
    const startTs = new Date(t.start_date).getTime();
    const endTs = t.end_date ? new Date(t.end_date).getTime() : Date.now();
    
    // Treatment overlaps if it started before window ends AND ended after window starts
    return startTs <= windowEnd && endTs >= windowStartTs;
  });
};

export default TimeWindowSelector;
